import { Box } from '@radix-ui/themes';
import { SuperAdminIcon } from 'components/common/custom-icon/shorthands';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonFormGrid } from 'components/common/form/grid';
import { CommonSearchInput } from 'components/common/form/search';
import { CommonSelectInput } from 'components/common/form/select';
import { CommonTextInput } from 'components/common/form/text';
import { GameDataPlayerSelector } from 'components/common/game-data-player-selector';
import { StatsApiVsManualToggle } from 'components/common/stats-api-vs-manual-toggle';
import useVideoLibrarySidebarStore from 'components/sections/video-library/sidebar/use-video-library-store';
import { PITCH_TYPE_FILTER_OPTIONS } from 'enums/filters';
import { StatsApiManualFormMode } from 'enums/forms';
import { GDPlayerSelectorMode } from 'enums/game-data.enums';
import useLatest from 'hooks/useLatestRef';
import { StaticVideoType } from 'lib_ts/enums/machine-msg.enum';
import {
  DELIVERY_TYPES,
  DeliveryType,
  PitchType,
} from 'lib_ts/enums/pitches.enums';
import { CAMERA_POSITIONS, CameraPosition } from 'lib_ts/enums/videos.enums';
import { first } from 'lodash';
import { useEffect, useState } from 'react';
import { useAuthStore } from 'stores/use-auth-store';
import { STATIC_PREFIX, useVideosStore } from 'stores/use-videos-store';

export const VideoLibrarySidebarEditor = () => {
  const userRoleFeatureFlags = useAuthStore((s) => s.userRoleFeatureFlags);
  const current = useAuthStore((s) => s.current);
  const leagueTeamOptions = useAuthStore((s) => s.leagueTeamOptions);

  const selectedVideo = useVideosStore((s) => s.selectedVideo);
  const latestSelectedVideo = useLatest(selectedVideo);

  const [formMode, setFormMode] = useState(StatsApiManualFormMode.Manual);

  // when video changes, re-evaluate which tab to use
  useEffect(() => {
    setFormMode(() => {
      if (!current.mlb_stats_api) {
        // user does not have access to Stats API
        return StatsApiManualFormMode.Manual;
      }

      if (!latestSelectedVideo.current) {
        // nothing selected, shouldn't trigger
        return StatsApiManualFormMode.StatsAPI;
      }

      const { mlb_pitcher, PitcherFullName, TeamName } =
        latestSelectedVideo.current;

      // user has access to Stats API
      const isManual = !mlb_pitcher && (!!PitcherFullName || !!TeamName);

      if (isManual) {
        // i.e. existing data was input manually
        return StatsApiManualFormMode.Manual;
      }

      return StatsApiManualFormMode.StatsAPI;
    });
  }, [current.mlb_stats_api, selectedVideo?._id]);

  const model = useVideoLibrarySidebarStore((s) => s.model);

  if (!selectedVideo) {
    return null;
  }

  if (!model) {
    return null;
  }

  return (
    <ErrorBoundary identifier="VideoLibrarySidebarEditor">
      <CommonFormGrid columns={2}>
        <Box gridColumn="span 2">
          <CommonFormGrid columns={1}>
            <StatsApiVsManualToggle
              mode={formMode}
              onChange={(v) => setFormMode(v)}
            />

            {formMode === StatsApiManualFormMode.StatsAPI && (
              <GameDataPlayerSelector
                mode={GDPlayerSelectorMode.Pitcher}
                model={model.mlb_pitcher}
                onChangeModel={(player) =>
                  useVideoLibrarySidebarStore.getState().mergeModel(
                    player
                      ? {
                          // copy details from player
                          PitcherFullName: player.name,
                          TeamName: player._mx_team_details?.name,
                          mlb_pitcher: {
                            season: player.season,
                            sportId: player.sportId,
                            playerPk: player.playerPk,
                          },
                        }
                      : {
                          // unset the selection
                          mlb_pitcher: null,
                        }
                  )
                }
                autoClose
              />
            )}

            {formMode === StatsApiManualFormMode.Manual && (
              <>
                <CommonTextInput
                  id="video-pitcher"
                  label="common.pitcher"
                  data-testid="Pitcher"
                  placeholder="Pitcher Name"
                  value={model.PitcherFullName ?? undefined}
                  onChange={(v) =>
                    useVideoLibrarySidebarStore.getState().mergeModel({
                      PitcherFullName: v,
                      mlb_pitcher: null, // changing details via manual entry should clear Stats API selection
                    })
                  }
                />

                <CommonSearchInput
                  id="video-team"
                  name="TeamName"
                  label="common.team"
                  values={model.TeamName ? [model.TeamName] : []}
                  options={leagueTeamOptions}
                  onChange={(v) =>
                    useVideoLibrarySidebarStore.getState().mergeModel({
                      TeamName: first(v) ?? null,
                      mlb_pitcher: null, // changing details via manual entry should clear Stats API selection
                    })
                  }
                  optional
                />
              </>
            )}
          </CommonFormGrid>
        </Box>

        <Box gridColumn="span 2">
          <CommonTextInput
            id="video-name"
            label="common.title"
            value={model.VideoTitle ?? ''}
            onChange={(v) =>
              useVideoLibrarySidebarStore
                .getState()
                .mergeModel({ VideoTitle: v })
            }
          />
        </Box>

        <CommonSearchInput
          id="video-pitch-type"
          name="PitchType"
          label="common.pitch-type"
          options={PITCH_TYPE_FILTER_OPTIONS}
          values={model.PitchType ? [model.PitchType] : []}
          onChange={(v) =>
            useVideoLibrarySidebarStore.getState().mergeModel({
              PitchType: first(v) as PitchType,
            })
          }
          optional
          skipSort
        />

        <CommonSelectInput
          id="video-delivery"
          name="DeliveryType"
          label="videos.pitch-delivery-type"
          options={DELIVERY_TYPES}
          value={model.DeliveryType ?? undefined}
          onChange={(v) =>
            useVideoLibrarySidebarStore
              .getState()
              .mergeModel({ DeliveryType: v as DeliveryType })
          }
          optional
        />

        <CommonSelectInput
          id="video-position"
          name="CameraPosition"
          label="videos.camera-position"
          value={model.CameraPosition ?? undefined}
          options={CAMERA_POSITIONS}
          onChange={(v) =>
            useVideoLibrarySidebarStore
              .getState()
              .mergeModel({ CameraPosition: v as CameraPosition })
          }
          optional
        />

        {userRoleFeatureFlags.isSuperAdmin &&
          selectedVideo.video_path.startsWith(STATIC_PREFIX) && (
            <CommonSelectInput
              id="video-static_type"
              name="type"
              label="Static Video Type"
              labelIcon={<SuperAdminIcon />}
              options={Object.values(StaticVideoType).map((o) => ({
                label: o,
                value: o,
              }))}
              value={model.static_type}
              onChange={(v) =>
                useVideoLibrarySidebarStore.getState().mergeModel({
                  static_type: v as StaticVideoType,
                })
              }
              optional
            />
          )}
      </CommonFormGrid>
    </ErrorBoundary>
  );
};
